import React from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import {
  DialogContentProps,
  DialogDescriptionProps,
  DialogProps,
  DialogTitleProps,
  DialogTriggerProps,
} from '@radix-ui/react-dialog';
import styled, { css, keyframes } from 'styled-components';
import classNames from 'classnames';
import { ButtonProps } from '../button/button';
import { SquareButton } from '../button';
import Icon from '../icon';
import { BoxProps, Div, Section } from '../box';
import ScrollableContainer from '../scrollable-container';
import Image from '../image';
import Panel, { PanelProps } from '../panel/panel';
import { deviceSizes } from '../../style';
import { heights, styleModes, widths } from './style';

const overlayShow = keyframes`
  0% {
    opacity: 0
  }

  100% {
    opacity: 1
  }
`;

export const StyledOverlay = styled(RadixDialog.Overlay)`
  background-color: rgba(0, 0, 0, 0.44);
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${overlayShow} 150ms cubic-bezier(0.16, 1, 0.3, 1);
  }
`;

const ModalContentBase = ({
  ref,
  ...props
}: Omit<ModalProps, 'show' | 'onOpenChange'> &
  React.HTMLAttributes<HTMLDivElement> & { ref?: React.RefObject<HTMLDivElement | null> }) => (
  <RadixDialog.Content ref={ref} aria-describedby={props['aria-describedby']} {...props} />
);

const StyledContent = styled(ModalContentBase).withConfig({
  shouldForwardProp: prop => !['overflowY', 'height', 'width', 'styleMode'].includes(prop),
})<{
  overflowY: ModalProps['overflowY'];
  height: keyof typeof heights;
  width: keyof typeof widths;
  styleMode?: keyof typeof styleModes;
}>`
  background-color: white;
  box-shadow:
    hsl(206 22% 7% / 35%) 0 10px 38px -10px,
    hsl(206 22% 7% / 20%) 0 10px 20px -15px;
  position: relative;
  z-index: 1300;
  border-radius: 2px;
  ${props => {
    const widthValue = widths[props.width];
    return typeof widthValue === 'string'
      ? css`
          width: ${widthValue};
        `
      : widthValue;
  }};
  height: ${props => heights[props.height]};
  max-height: calc(100% - 64px);
  overflow: hidden;
  overflow-y: ${props => props.overflowY || 'auto'};
  ${props => props.styleMode && styleModes[props.styleMode]};

  &:focus {
    outline: none;
  }

  @media (prefers-reduced-motion: no-preference) {
    animation: ${overlayShow} 150ms cubic-bezier(0.16, 1, 0.3, 1);
  }

  @media (max-width: ${deviceSizes.sm}) {
    width: 95% !important;
  }
`;

export interface ModalProps extends DialogProps {
  'aria-label'?: string;
  'aria-labelledby'?: string;
  className?: string;
  'aria-describedby'?: string;
  width?: keyof typeof widths;
  height?: keyof typeof heights;
  show: boolean;
  onHide?: () => void;
  'data-name'?: string;
  'data-testid'?: string;
  children?: React.ReactNode;
  overflowY?: 'auto' | 'hidden' | 'visible';
  withBackDrop?: boolean;
  styleMode?: keyof typeof styleModes;
  onInteractOutside?: DialogContentProps['onInteractOutside'];
  onOpenAutoFocus?: DialogContentProps['onOpenAutoFocus'];
  onCloseAutoFocus?: DialogContentProps['onCloseAutoFocus'];
}

const Modal: React.FunctionComponent<ModalProps> & {
  SquareCloseButton: React.FunctionComponent<CloseButtonProps>;
  BackButton: React.FunctionComponent<CloseButtonProps>;
  Footer: React.FunctionComponent<PanelProps>;
  ScrollableContent: React.FunctionComponent<BoxProps>;
  CoverPhoto: React.FunctionComponent<ModalCoverPhotoProps>;
  Title: React.FunctionComponent<DialogTitleProps>;
  Description: React.FunctionComponent<DialogDescriptionProps>;
  Trigger: React.FunctionComponent<DialogTriggerProps>;
} = ({
  children,
  show,
  onHide,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy = undefined,
  withBackDrop = true,
  overflowY = 'auto',
  width = 'lg',
  height = 'default',
  className,
  'data-testid': dataTestId,
  'data-name': dataName,
  styleMode,
  onOpenAutoFocus,
  onInteractOutside = e => e.preventDefault(),
  onCloseAutoFocus,
}: ModalProps) => (
  <RadixDialog.Root
    open={show}
    onOpenChange={open => {
      if (!open) {
        onHide?.();
      }
    }}
  >
    <RadixDialog.Portal>
      {withBackDrop && (
        <StyledOverlay className={'modal-backdrop'}>
          <StyledContent
            onOpenAutoFocus={onOpenAutoFocus}
            aria-modal={true}
            data-name={dataName}
            data-testid={dataTestId}
            data-slot="modal-content"
            onInteractOutside={onInteractOutside}
            className={className}
            width={width}
            height={height}
            styleMode={styleMode}
            overflowY={overflowY}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            onCloseAutoFocus={onCloseAutoFocus}
            aria-describedby={ariaDescribedBy}
          >
            {children}
          </StyledContent>
        </StyledOverlay>
      )}
    </RadixDialog.Portal>
  </RadixDialog.Root>
);

/******************************
 * Sub components
 *****************************/
interface CloseButtonProps extends ButtonProps {
  placement?: 'right' | 'left';
  position?: 'fixed' | 'absolute' | 'static';
  onHide?: () => void;
}

const ModalSquareButton = styled(
  ({
    onHide,
    onClick,
    size = 'md',
    className,
    placement = 'right',
    children,
    'data-testid': dataTestId = 'modal-square-button',
    'aria-label': ariaLabel,
    variant = 'secondary-clean',
    componentClass,
    to,
  }: CloseButtonProps) => (
    <SquareButton
      to={to}
      aria-label={ariaLabel || 'close'}
      variant={variant}
      className={classNames('modal-square-button', className, placement)}
      size={size}
      onClick={onClick || onHide}
      data-testid={dataTestId}
      componentClass={componentClass}
    >
      {children || <Icon name={'CLOSE'} size={'xxl'} variant={'inherit'} />}
    </SquareButton>
  ),
)<CloseButtonProps>`
  font-size: 1.875rem;
  z-index: 3000;
  position: ${props => props.position || 'absolute'};
  top: 10px;

  &.left {
    left: 10px;
  }

  &.right {
    right: 10px;
  }
`;

const ModalBackButton = ({ size, ...rest }: CloseButtonProps) => (
  <ModalSquareButton placement={'left'} size={size} {...rest}>
    <Icon size={'xxl'} name={'ARROW_LEFT'} variant={'inherit'} />
  </ModalSquareButton>
);

export const ScrollableContent = ({ children, className, style, ...rest }: BoxProps) => (
  <Div
    style={{ height: '100%', ...style }}
    className={classNames('scrollable-content', className)}
    {...rest}
    componentClass={ScrollableContainer}
  >
    {children}
  </Div>
);

interface ModalCoverPhotoProps extends BoxProps {
  photo: string;
}

const CoverPhoto = ({ className, photo, ...rest }: ModalCoverPhotoProps) => (
  <Section className={classNames('modal-cover-photo', className)} {...rest}>
    <Image height={100} objectFit={'cover'} width={'100%'} alt={'modal-header-photo'} src={photo} />
  </Section>
);

const ModalFooter = ({
  position = 'static',
  variant = 'gray100',
  justifyContent = 'flex-end',
  paddingHorizontal = 'md',
  paddingVertical = 'md',
  ...rest
}: PanelProps) => (
  <Panel
    flexGrow={1}
    display={'flex'}
    alignItems={'center'}
    justifyContent={justifyContent}
    variant={variant}
    paddingHorizontal={paddingHorizontal}
    paddingVertical={paddingVertical}
    position={position}
    style={{
      left: 0,
      right: 0,
      width: '100%',
      bottom: 0,
    }}
    {...rest}
  />
);

const ModalTitle = ({ asChild = true, children, ...rest }: DialogTitleProps) => (
  <RadixDialog.Title asChild={asChild} {...rest}>
    {children}
  </RadixDialog.Title>
);

const ModalDescription = ({ asChild = true, children, ...rest }: DialogDescriptionProps) => (
  <RadixDialog.Description asChild={asChild} {...rest}>
    {children}
  </RadixDialog.Description>
);

/******************************
 * Exports
 *****************************/
Modal.SquareCloseButton = ModalSquareButton;
Modal.BackButton = ModalBackButton;
Modal.Footer = ModalFooter;
Modal.ScrollableContent = ScrollableContent;
Modal.CoverPhoto = CoverPhoto;
Modal.Title = ModalTitle;
Modal.Description = ModalDescription;
Modal.Trigger = RadixDialog.Trigger;
export default Modal;
